import React, { useState } from 'react';
import { X, Plus, UserPlus, Copy, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Session, SessionSettings } from '../types';
import ApprovalWaitingScreen from './ApprovalWaitingScreen';

interface SessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'join';
}

const SessionModal: React.FC<SessionModalProps> = ({ isOpen, onClose, mode }) => {
  const { state, dispatch } = useApp();
  const [sessionName, setSessionName] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [maxUsers, setMaxUsers] = useState(5);
  const [sessionSettings, setSessionSettings] = useState<SessionSettings>({
    autoClearOnJoin: false,
    autoClearOnReload: false,
    allowAnonymousUsers: true,
    requireApproval: false,
    maxHistoryItems: 50,
    maxChatMessages: 100,
  });
  const [copied, setCopied] = useState(false);
  const [showApprovalWaiting, setShowApprovalWaiting] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState('');

  const generateSessionId = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleCreateSession = async () => {
    if (!sessionName.trim()) {
      alert('Please enter a session name');
      return;
    }

    if (!state.currentUser.id) {
      alert('Please set up your profile first');
      return;
    }

    try {
      // Import sessionService dynamically to avoid circular imports
      const { sessionService } = await import('../services/sessionService');
      
      // Save user profile to Firestore
      await sessionService.saveUserProfile(state.currentUser);
      
      // Create session in Firestore
      const sessionId = generateSessionId();
      const sessionData = {
        name: sessionName.trim(),
        createdBy: state.currentUser.id,
        maxUsers,
        settings: sessionSettings,
      };

      await sessionService.createSessionWithId(sessionId, sessionData);
      
      // Convert to local session format
      const newSession: Session = {
        id: sessionId,
        name: sessionName.trim(),
        createdBy: state.currentUser.id,
        createdAt: new Date(),
        isActive: true,
        maxUsers,
        settings: sessionSettings,
      };

      dispatch({ type: 'CREATE_SESSION', payload: newSession });
      onClose();
      setSessionName('');
      setMaxUsers(5);
    } catch (error) {
      console.error('Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    }
  };

  const handleJoinSession = async () => {
    if (!sessionId.trim()) {
      alert('Please enter a session ID');
      return;
    }

    if (!state.currentUser.id) {
      alert('Please set up your profile first');
      return;
    }

    try {
      // Import sessionService dynamically to avoid circular imports
      const { sessionService } = await import('../services/sessionService');
      
      // Try to get the session first to validate it exists
      const session = await sessionService.getSession(sessionId.trim());
      
      if (!session) {
        alert('Session not found. Please check the Session ID and try again.');
        return;
      }

      if (!session.isActive) {
        alert('This session is no longer active.');
        return;
      }

      if (session.currentUsers.length >= session.maxUsers) {
        alert('This session is full.');
        return;
      }

      // Save user profile to Firestore
      await sessionService.saveUserProfile(state.currentUser);
      
      // Join the session
      await sessionService.joinSession(sessionId.trim(), state.currentUser.id);
      
      // Convert Firestore session to local session format
      const localSession: Session = {
        id: session.id,
        name: session.name,
        createdBy: session.createdBy,
        createdAt: session.createdAt.toDate(),
        isActive: session.isActive,
        maxUsers: session.maxUsers,
        settings: session.settings,
      };

      dispatch({ 
        type: 'JOIN_SESSION', 
        payload: { 
          session: localSession, 
          user: state.currentUser 
        } 
      });
      
      onClose();
      setSessionId('');
    } catch (error) {
      console.error('Failed to join session:', error);
      
      // Handle approval pending case
      if (error instanceof Error && error.message.includes('JOIN_REQUEST_PENDING_APPROVAL')) {
        setPendingSessionId(sessionId.trim());
        setShowApprovalWaiting(true);
        setSessionId('');
        return;
      }
      
      alert('Failed to join session. Please check the Session ID and try again.');
    }
  };

  const copySessionId = () => {
    if (state.currentSession) {
      navigator.clipboard.writeText(state.currentSession.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApprovalApproved = async () => {
    try {
      // Re-trigger the join session flow now that user is approved
      const { sessionService } = await import('../services/sessionService');
      
      // Get the session data
      const session = await sessionService.getSession(pendingSessionId);
      
      if (session) {
        // Convert Firestore session to local session format
        const localSession: Session = {
          id: session.id,
          name: session.name,
          createdBy: session.createdBy,
          createdAt: session.createdAt.toDate(),
          isActive: session.isActive,
          maxUsers: session.maxUsers,
          settings: session.settings,
        };

        // Dispatch JOIN_SESSION action
        dispatch({ 
          type: 'JOIN_SESSION', 
          payload: { 
            session: localSession, 
            user: state.currentUser 
          } 
        });
      }
    } catch (error) {
      console.error('Failed to join session after approval:', error);
    } finally {
      setShowApprovalWaiting(false);
      setPendingSessionId('');
      onClose();
    }
  };

  const handleApprovalRejected = () => {
    setShowApprovalWaiting(false);
    setPendingSessionId('');
    onClose();
  };

  if (!isOpen) return null;

  // Show approval waiting screen if needed
  if (showApprovalWaiting) {
    return (
      <ApprovalWaitingScreen
        sessionId={pendingSessionId}
        onApproved={handleApprovalApproved}
        onRejected={handleApprovalRejected}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-xl w-full max-w-md shadow-2xl border border-dark-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="flex items-center space-x-3">
            {mode === 'create' ? (
              <Plus className="h-6 w-6 text-accent-500" />
            ) : (
              <UserPlus className="h-6 w-6 text-accent-500" />
            )}
            <h2 className="text-xl font-bold text-white">
              {mode === 'create' ? 'Create Session' : 'Join Session'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-dark-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {mode === 'create' ? (
            <>
              <div>
                <label className="text-white font-medium">Session Name</label>
                <input
                  type="text"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="Enter session name"
                  className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500 mt-2"
                />
              </div>

              <div>
                <label className="text-white font-medium">Max Users</label>
                <select
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(parseInt(e.target.value))}
                  className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500 mt-2"
                >
                  <option value={2}>2 users</option>
                  <option value={3}>3 users</option>
                  <option value={4}>4 users</option>
                  <option value={5}>5 users</option>
                  <option value={6}>6 users</option>
                  <option value={8}>8 users</option>
                  <option value={10}>10 users</option>
                </select>
              </div>

              <div className="space-y-3">
                <h4 className="text-white font-medium">Session Settings</h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-white text-sm">Auto Clear on Join</label>
                    <p className="text-xs text-dark-400">Clear data when new users join</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sessionSettings.autoClearOnJoin}
                      onChange={(e) => setSessionSettings({ ...sessionSettings, autoClearOnJoin: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-white text-sm">Allow Anonymous Users</label>
                    <p className="text-xs text-dark-400">Allow users without profiles to join</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sessionSettings.allowAnonymousUsers}
                      onChange={(e) => setSessionSettings({ ...sessionSettings, allowAnonymousUsers: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-white text-sm">Require Approval</label>
                    <p className="text-xs text-dark-400">Require approval for calculations</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sessionSettings.requireApproval}
                      onChange={(e) => setSessionSettings({ ...sessionSettings, requireApproval: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
                  </label>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-white font-medium">Session ID</label>
                <input
                  type="text"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  placeholder="Enter session ID"
                  className="w-full bg-dark-700 text-white rounded-lg px-3 py-2 border border-dark-600 focus:outline-none focus:ring-2 focus:ring-accent-500 mt-2"
                />
              </div>

              <div className="bg-dark-700 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2">How to join a session:</h4>
                <ol className="text-sm text-dark-300 space-y-1">
                  <li>1. Ask the session creator for the Session ID</li>
                  <li>2. Enter the Session ID above</li>
                  <li>3. Click "Join Session"</li>
                </ol>
              </div>
            </>
          )}

          {state.currentSession && (
            <div className="bg-accent-900 border border-accent-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">Current Session</h4>
                  <p className="text-sm text-accent-300">{state.currentSession.name}</p>
                  <p className="text-xs text-accent-400">ID: {state.currentSession.id}</p>
                </div>
                <button
                  onClick={copySessionId}
                  className="flex items-center space-x-2 px-3 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg transition-all duration-200"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span className="text-sm">{copied ? 'Copied!' : 'Copy ID'}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-dark-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-700 transition-all duration-200 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={mode === 'create' ? handleCreateSession : handleJoinSession}
            className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-all duration-200 font-medium shadow-md"
          >
            {mode === 'create' ? 'Create Session' : 'Join Session'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionModal;
